import {
    FC,
    getProp,
    CanBeSignal,
    JSX,
    PointLike,
    iterChildren,
    copyPointData,
} from '@gongxigames/framework/core/fragments/BuilderY';
import {Store} from '@gongxigames/framework/core/fragments/store';
import {slotStore, strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {Rectangle, Point, Container as PixiContainer} from 'pixi.js';

type FullScreenAlignProps = {
    align: CanBeSignal<PointLike>;
    offset?: CanBeSignal<PointLike>;
    children: JSX.Element;
};

/**
 * A component that helps to get a point on the screen,
 * in the screen coordinate space.
 * It must contain one child container which will control.
 *
 * align [0,0] - will be the top left corner of the screen
 * align [1,1] - will be the bottom right corner of the screen
 * align [0.5, 0.5] - will be the center of the screen
 */
export const $FullScreenAlign: FC<FullScreenAlignProps> = (props) => {
    const tmpRectangle = new Rectangle();
    const tmpAlign = new Point();
    const tmpOffset = new Point();
    const container = getProp(props.children);

    function update() {
        const safeZone = slotStore.stage_safeZoneState_game.value!;
        const align = getProp(props.align);
        const offset = getProp(props.offset) ?? 0;

        copyPointData(align, tmpAlign);
        copyPointData(offset, tmpOffset);
        safeZone.calcInSafeFader(tmpRectangle);

        tmpRectangle.width *= tmpAlign.x;
        tmpRectangle.height *= tmpAlign.y;

        const x = tmpRectangle.x + tmpRectangle.width + tmpOffset.x;
        const y = tmpRectangle.y + tmpRectangle.height + tmpOffset.y;

        for (const child of iterChildren(props.children)) {
            child.position.set(x, y);
        }
    }

    if (!(container instanceof PixiContainer)) {
        throw new Error('FullScreenAlign: shod contain only one child element');
    }

    const offUpdateAlign = Store.effect(() => update());
    const offUpdateResize = strictUiEmitter.on('CORE.screen.resize', () => update());

    container.on('destroyed', offUpdateAlign);
    container.on('destroyed', offUpdateResize);

    return container;
};